import { supabase } from '../../lib/supabase';
import { IProductRepository } from '../../domain/repositories/IProductRepository';
import { Product } from '../../domain/entities/Product';
import { InfrastructureError } from '../../core/errors/DomainErrors';

export class SupabaseProductRepository implements IProductRepository {
    async findAll(organizationId: string): Promise<Product[]> {
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[REPOSITORY] Error fetching products:', error);
            throw new InfrastructureError(`Erro ao buscar produtos: ${error.message}`, error);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string): Promise<Product | null> {
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('id', id)
            .single();

        if (error) {
            if (error.code === 'PGRST116') {
                return null; // Not found
            }
            console.error('[REPOSITORY] Error fetching product:', error);
            throw new InfrastructureError(`Erro ao buscar produto: ${error.message}`, error);
        }

        return this.mapToEntity(data);
    }

    async findByName(name: string, organizationId: string): Promise<Product[]> {
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('organization_id', organizationId)
            .ilike('name', `%${name}%`)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[REPOSITORY] Error searching products by name:', error);
            return [];
        }

        return (data || []).map(this.mapToEntity);
    }

    async save(product: Product, organizationId: string, userId?: string): Promise<Product> {
        const dbProduct = this.mapToDatabase(product);

        // Verifica se é novo (ID temporário)
        const isNew = product.id.startsWith('temp_');

        if (isNew) {
            // Remove ID temporário para Supabase gerar UUID
            const { id, ...productWithoutId } = dbProduct;

            const productToInsert = {
                ...productWithoutId,
                organization_id: organizationId,
                created_by: userId || null
            };

            const { data, error } = await supabase
                .from('products')
                .insert([productToInsert])
                .select()
                .single();

            if (error) {
                console.error('[REPOSITORY] Error creating product:', error);
                throw new InfrastructureError(`Erro ao criar produto: ${error.message}`, error);
            }

            return this.mapToEntity(data);
        } else {
            // Remove o ID do objeto de atualização (não deve ser atualizado)
            const { id, created_at, created_by, ...updateData } = dbProduct;

            const { data, error } = await supabase
                .from('products')
                .update(updateData)
                .eq('id', product.id)
                .select()
                .single();

            if (error) {
                console.error('[REPOSITORY] Error updating product:', error);
                throw new InfrastructureError(`Erro ao atualizar produto: ${error.message}`, error);
            }

            return this.mapToEntity(data);
        }
    }

    async delete(id: string): Promise<void> {
        const { data, error } = await supabase
            .from('products')
            .delete()
            .eq('id', id)
            .select();

        if (error) {
            console.error('[REPOSITORY] Error deleting product:', error);
            throw new InfrastructureError(`Erro ao deletar produto: ${error.message}`, error);
        }

        if (!data || data.length === 0) {
            throw new InfrastructureError('Produto não encontrado ou já foi deletado');
        }
    }

    async count(organizationId: string): Promise<number> {
        const { count, error } = await supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[REPOSITORY] Error counting products:', error);
            return 0;
        }

        return count || 0;
    }

    /**
     * Mapeia dados do banco para entidade de domínio
     */
    private mapToEntity(data: any): Product {
        return new Product(
            data.id,
            data.name,
            parseFloat(data.value) || 0,
            data.description || '',
            new Date(data.created_at),
            new Date(data.updated_at),
            data.created_by
        );
    }

    /**
     * Mapeia entidade de domínio para formato do banco
     */
    private mapToDatabase(product: Product): any {
        return {
            id: product.id,
            name: product.name,
            value: product.getValue(),
            description: product.description,
            created_at: product.createdAt.toISOString(),
            updated_at: new Date().toISOString(),
            created_by: product.createdBy || null
        };
    }
}
